import {FormEvent, useEffect, useRef, useState} from 'react';
import {Alert, Button, Stack, TextField} from '@mui/material';
import {resendConfirmationEmail} from '../../services/EmailConfirmationClient';
import {normalizeApiError} from '../../common/apiErrors';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
export default function ResendConfirmation({initialEmail = '', fixedEmail = false}: {initialEmail?: string; fixedEmail?: boolean}) {
    const {t} = useAppTranslation();
    const [email, setEmail] = useState(initialEmail);
    const [pending, setPending] = useState(false);
    const busy = useRef(false);
    const availableAt = useRef(0);
    const [secondsLeft, setSecondsLeft] = useState(0);
    useEffect(() => {
        if (!secondsLeft) return;
        const timer = window.setInterval(() => {
            setSecondsLeft(Math.max(0, Math.ceil((availableAt.current - Date.now()) / 1000)));
        }, 250);
        return () => window.clearInterval(timer);
    }, [secondsLeft]);
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const startCooldown = (seconds: number) => {
        availableAt.current = Date.now() + seconds * 1000;
        setSecondsLeft(seconds);
    };
    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (busy.current || Date.now() < availableAt.current) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {setSuccess(false); setMessage('email_invalid'); return;}
        busy.current = true; setPending(true); setMessage('');
        try {
            const status = await resendConfirmationEmail(email.trim());
            if (status === 202) startCooldown(60);
            setSuccess(true); setMessage('confirmation_sent');
        }
        catch (error) {
            const problem = normalizeApiError(error);
            if (problem.status === 429 && problem.code === 'email.confirmation_cooldown') {
                const seconds = (error as {retryAfterSeconds?: number}).retryAfterSeconds;
                if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) startCooldown(seconds);
            }
            setSuccess(false); setMessage(problem.status === 429 ? 'rate_limited' : 'confirmation_error');
        }
        finally {busy.current = false; setPending(false);}
    };
    return <Stack component="form" onSubmit={submit} spacing={2} sx={{'& input': {cursor: 'default'}}}>
        {!fixedEmail && <TextField required type="email" label={t('registration:email')} value={email} disabled={pending} onChange={e => {setEmail(e.target.value); setMessage('');}}/>}
        {message && <Alert severity={success ? 'info' : 'error'}>{t(`registration:${message}` as never)}</Alert>}
        <Button type="submit" disabled={pending || secondsLeft > 0}>{secondsLeft > 0
            ? String(t('registration:resend_countdown' as never, {seconds: secondsLeft} as never))
            : t('registration:resend_confirmation' as never)}</Button>
    </Stack>;
}
